import { describeDeltaE, srgbToHex, srgbToLab, type SRGB } from '../color/index.js';
import {
  assessIllumination,
  buildRelativeCard,
  fitCalibration,
  measure,
  recommendModel,
  SWATCH_CATALOG,
  type Calibration,
  type IlluminationProbe,
  type Measurement,
  type ReferenceCard,
} from '../calibration/index.js';
import {
  captureFrame,
  clientPointToFrame,
  decodeImageFile,
  isCameraSupported,
  sampleRegion,
  sampleSizeFor,
  startCamera,
  stopCamera,
  uniformityScore,
} from '../capture/index.js';

/** Above this linear-light standard deviation, a region is not one flat surface. */
const UNIFORMITY_WARN = 0.05;

interface State {
  stream: MediaStream | null;
  /** Baseline captures keyed by catalog patch id. */
  baseline: Map<string, SRGB>;
  /** Which patch the next tap records; null means measure mode. */
  pendingPatchId: string | null;
  card: ReferenceCard | null;
  calibration: Calibration | null;
  /** Why the current model was chosen; surfaced so the limitation is visible. */
  modelReason: string | null;
  lastMeasurement: Measurement | null;
  /**
   * A loaded still, held decoded so repeated taps do not re-decode a 12 MP
   * image. Mutually exclusive with a live camera stream.
   */
  photo: { name: string; frame: ImageData } | null;
  /** When true, taps collect illumination probes instead of taking readings. */
  probingLight: boolean;
  /** Readings of one physically uniform surface at different frame positions. */
  lightProbes: IlluminationProbe[];
  error: string | null;
}

export function mountApp(root: HTMLElement): void {
  const state: State = {
    stream: null,
    baseline: new Map(),
    pendingPatchId: null,
    card: null,
    calibration: null,
    modelReason: null,
    lastMeasurement: null,
    photo: null,
    probingLight: false,
    lightProbes: [],
    error: null,
  };

  root.innerHTML = `
    <header>
      <h1>Color Sampler</h1>
      <span class="provenance" data-state="none">Uncalibrated</span>
    </header>

    <div class="viewport" data-active="false" data-source="none">
      <video playsinline muted></video>
      <canvas class="still"></canvas>
      <div class="viewport-empty">
        Start the camera or open a photo, then tap a swatch to sample it.
      </div>
      <div class="reticle"></div>
    </div>

    <p class="error hidden"></p>

    <div class="controls">
      <button class="primary" data-action="toggle-camera">Start camera</button>
      <button data-action="open-photo">Open photo</button>
      <button data-action="check-light" disabled>Check lighting</button>
      <button data-action="reset" disabled>Reset calibration</button>
      <input type="file" accept="image/*" class="file-input" hidden />
    </div>

    <p class="note source-note"></p>

    <section class="light hidden">
      <h2>Lighting check</h2>
      <p class="light-status"></p>
      <p class="note light-advice"></p>
    </section>

    <section>
      <h2>Reference card &mdash; relative baseline</h2>
      <div class="patch-list"></div>
      <p class="note patch-note"></p>
    </section>

    <section class="sample hidden">
      <h2>Sample</h2>
      <div class="sample-header">
        <span class="chip"></span>
        <div class="swatch-meta"></div>
      </div>
      <dl class="readout"></dl>
    </section>
  `;

  const el = {
    provenance: query<HTMLSpanElement>(root, '.provenance'),
    viewport: query<HTMLDivElement>(root, '.viewport'),
    video: query<HTMLVideoElement>(root, 'video'),
    still: query<HTMLCanvasElement>(root, 'canvas.still'),
    fileInput: query<HTMLInputElement>(root, '.file-input'),
    openPhoto: query<HTMLButtonElement>(root, '[data-action="open-photo"]'),
    sourceNote: query<HTMLParagraphElement>(root, '.source-note'),
    viewportEmpty: query<HTMLDivElement>(root, '.viewport-empty'),
    reticle: query<HTMLDivElement>(root, '.reticle'),
    error: query<HTMLParagraphElement>(root, '.error'),
    toggleCamera: query<HTMLButtonElement>(root, '[data-action="toggle-camera"]'),
    checkLight: query<HTMLButtonElement>(root, '[data-action="check-light"]'),
    reset: query<HTMLButtonElement>(root, '[data-action="reset"]'),
    lightSection: query<HTMLElement>(root, 'section.light'),
    lightStatus: query<HTMLParagraphElement>(root, '.light-status'),
    lightAdvice: query<HTMLParagraphElement>(root, '.light-advice'),
    patchList: query<HTMLDivElement>(root, '.patch-list'),
    note: query<HTMLParagraphElement>(root, '.patch-note'),
    sampleSection: query<HTMLElement>(root, 'section.sample'),
    sampleChip: query<HTMLSpanElement>(root, '.sample-header .chip'),
    swatchMeta: query<HTMLDivElement>(root, '.swatch-meta'),
    readout: query<HTMLDListElement>(root, '.readout'),
  };

  el.toggleCamera.addEventListener('click', () => {
    void (state.stream ? stop() : start());
  });

  el.openPhoto.addEventListener('click', () => el.fileInput.click());

  el.fileInput.addEventListener('change', () => {
    const file = el.fileInput.files?.[0];
    // Reset so re-picking the same file fires another change event.
    el.fileInput.value = '';
    if (file) void loadPhoto(file);
  });

  el.checkLight.addEventListener('click', () => {
    state.probingLight = !state.probingLight;
    if (state.probingLight) {
      state.lightProbes = [];
      state.pendingPatchId = null;
    }
    render();
  });

  el.reset.addEventListener('click', () => {
    state.baseline.clear();
    state.card = null;
    state.calibration = null;
    state.modelReason = null;
    state.lastMeasurement = null;
    state.pendingPatchId = null;
    render();
  });

  el.viewport.addEventListener('click', (event) => onViewportClick(event));

  async function start(): Promise<void> {
    if (!isCameraSupported()) {
      state.error =
        'This browser cannot access a camera. getUserMedia needs a secure context — use https, or localhost in development.';
      render();
      return;
    }
    try {
      state.error = null;
      state.stream = await startCamera({ facingMode: 'environment' });
      el.video.srcObject = state.stream;
      await el.video.play();
      // A live stream replaces any loaded still.
      state.photo = null;
      state.lightProbes = [];
    } catch (error) {
      state.stream = null;
      state.error = error instanceof Error ? error.message : String(error);
    }
    render();
  }

  /**
   * Load a still and make it the active source.
   *
   * The decoded pixels are painted into the visible canvas rather than shown
   * via an <img>, so what is displayed is byte-for-byte what gets sampled —
   * no chance of the browser applying an orientation or colour transform to
   * one and not the other.
   */
  async function loadPhoto(file: File): Promise<void> {
    try {
      state.error = null;
      const frame = await decodeImageFile(file);

      // A still and a live stream cannot both be the source.
      stopCamera(state.stream);
      state.stream = null;
      el.video.srcObject = null;

      el.still.width = frame.width;
      el.still.height = frame.height;
      const context = el.still.getContext('2d');
      if (!context) throw new Error('Could not acquire a 2D canvas context.');
      context.putImageData(frame, 0, 0);

      state.photo = { name: file.name, frame };
      // A different scene invalidates the previous reading and probes.
      state.lastMeasurement = null;
      state.lightProbes = [];
      el.reticle.dataset['visible'] = 'false';
    } catch (error) {
      state.photo = null;
      state.error = error instanceof Error ? error.message : String(error);
    }
    render();
  }

  async function stop(): Promise<void> {
    stopCamera(state.stream);
    state.stream = null;
    el.video.srcObject = null;
    render();
  }

  function onViewportClick(event: MouseEvent): void {
    const live = state.stream !== null;
    if (!live && !state.photo) return;

    const frameWidth = live ? el.video.videoWidth : state.photo!.frame.width;
    const frameHeight = live ? el.video.videoHeight : state.photo!.frame.height;

    const point = clientPointToFrame(
      el.viewport,
      event.clientX,
      event.clientY,
      frameWidth,
      frameHeight,
    );
    // Null means the tap landed in the letterbox bars, not on the image.
    if (!point) return;

    try {
      const frame = live ? captureFrame(el.video) : state.photo!.frame;
      const sample = sampleRegion(frame, {
        x: point.x,
        y: point.y,
        size: sampleSizeFor(frameWidth),
      });
      state.error = null;

      positionReticle(event);

      if (state.probingLight) {
        // Probes must all be of the SAME uniform surface; the user is told to
        // tap several spots on one card. Position is what makes the reading
        // meaningful, so it is recorded alongside.
        state.lightProbes.push({
          x: point.x,
          y: point.y,
          lab: srgbToLab(sample.srgb),
        });
        renderSample(sample.srgb, uniformityScore(sample), sample.clippedFraction);
        render();
        return;
      }

      if (state.pendingPatchId) {
        state.baseline.set(state.pendingPatchId, sample.srgb);
        state.pendingPatchId = null;
        rebuildCalibration();
      } else if (state.calibration) {
        state.lastMeasurement = measure(state.calibration, sample.srgb, state.card ?? undefined);
      }

      renderSample(sample.srgb, uniformityScore(sample), sample.clippedFraction);
    } catch (error) {
      state.error = error instanceof Error ? error.message : String(error);
    }
    render();
  }

  function positionReticle(event: MouseEvent): void {
    const rect = el.viewport.getBoundingClientRect();
    el.reticle.style.left = `${event.clientX - rect.left}px`;
    el.reticle.style.top = `${event.clientY - rect.top}px`;
    el.reticle.dataset['visible'] = 'true';
  }

  /**
   * Refit whenever the baseline changes. The model steps up automatically as
   * patches accumulate: a 3x3 fit needs three, so with fewer we fall back to a
   * per-channel gain rather than refusing to calibrate at all.
   */
  function rebuildCalibration(): void {
    if (state.baseline.size === 0) {
      state.card = null;
      state.calibration = null;
      state.modelReason = null;
      return;
    }

    const samples = SWATCH_CATALOG.filter((spec) => state.baseline.has(spec.id)).map((spec) => ({
      id: spec.id,
      name: spec.name,
      srgb: state.baseline.get(spec.id) as SRGB,
    }));

    state.card = buildRelativeCard({
      id: 'session-baseline',
      name: 'Session baseline',
      samples,
      notes: 'Captured in this session; targets are camera readings, not verified values.',
    });

    // Chosen by conditioning, not patch count. Three near-neutral swatches
    // clear the count threshold for a 3x3 fit but cannot support one.
    const recommendation = recommendModel(state.card);
    state.modelReason = recommendation.reason;

    try {
      state.calibration = fitCalibration(
        state.card,
        samples.map((s) => ({ patchId: s.id, srgb: s.srgb })),
        recommendation.model,
      );
    } catch (error) {
      state.calibration = null;
      state.error = error instanceof Error ? error.message : String(error);
    }
  }

  function renderSample(srgb: SRGB, uniformity: number, clippedFraction: number): void {
    el.sampleSection.classList.remove('hidden');
    el.sampleChip.style.backgroundColor = srgbToHex(srgb);
    el.sampleChip.style.backgroundImage = 'none';

    const measurement = state.lastMeasurement;
    const rows: Array<[string, string]> = [['Raw sRGB', srgbToHex(srgb)]];

    if (measurement) {
      rows.push(
        ['Raw Lab', formatLab(measurement.raw.lab)],
        ['Calibrated Lab', formatLab(measurement.calibrated.lab)],
        ['Calibrated sRGB', srgbToHex(measurement.calibrated.srgb)],
      );
      if (measurement.nearest) {
        rows.push([
          'Nearest patch',
          `${measurement.nearest.name} — ΔE₀₀ ${measurement.nearest.deltaE.toFixed(2)} (${describeDeltaE(
            measurement.nearest.deltaE,
          )})`,
        ]);
      }
    }

    if (uniformity > UNIFORMITY_WARN) {
      rows.push([
        'Uniformity',
        `σ ${uniformity.toFixed(3)} — region is not a flat surface; re-sample away from edges and highlights`,
      ]);
    }

    if (clippedFraction > 0) {
      // A clipped pixel has no recoverable value, so this is a correctness
      // problem rather than a quality one — say so even at low fractions.
      rows.push([
        'Clipping',
        `${(clippedFraction * 100).toFixed(1)}% of pixels hit the black or white limit — ` +
          `their true colour is unrecoverable. Reduce exposure or move the highlight off the swatch.`,
      ]);
    }

    el.swatchMeta.textContent = measurement
      ? measurement.provenance === 'relative'
        ? 'Relative to this session’s baseline.'
        : 'Absolute measurement.'
      : 'Uncalibrated reading.';

    el.readout.innerHTML = rows
      .map(
        ([term, value]) =>
          `<dt>${escapeHtml(term)}</dt><dd${
            term === 'Uniformity' ? ' class="warning"' : term === 'Clipping' ? ' class="danger"' : ''
          }>${escapeHtml(value)}</dd>`,
      )
      .join('');
  }

  /**
   * The lighting check answers a question no single sample can: is the light
   * even across the frame? Uneven light defeats a global correction entirely,
   * and it is invisible to the per-region uniformity score.
   */
  function renderLighting(): void {
    const show = state.probingLight || state.lightProbes.length > 0;
    el.lightSection.classList.toggle('hidden', !show);
    if (!show) return;

    const count = state.lightProbes.length;
    if (count < 2) {
      el.lightStatus.textContent =
        `${count} of at least 2 probes. Tap several well-separated spots on ONE card — ` +
        `corners and centre. The card is physically uniform, so anything that differs ` +
        `between those spots is the lighting, not the paint.`;
      el.lightStatus.className = 'light-status';
      el.lightAdvice.textContent = '';
      return;
    }

    const assessment = assessIllumination(state.lightProbes);
    const label =
      assessment.verdict === 'good' ? 'EVEN' : assessment.verdict === 'marginal' ? 'UNEVEN' : 'TOO UNEVEN';

    el.lightStatus.textContent =
      `${label} — ${count} probes, worst ΔE₀₀ ${assessment.worstDeltaE.toFixed(1)}, ` +
      `L* spread ${assessment.lightnessSpread.toFixed(1)}` +
      (assessment.gradient
        ? `, gradient ${assessment.gradient.perThousandPx.toFixed(1)} L* per 1000 px`
        : '');
    el.lightStatus.className =
      'light-status ' + (assessment.verdict === 'good' ? 'ok' : assessment.verdict === 'marginal' ? 'warning' : 'danger');
    el.lightAdvice.textContent = assessment.advice;
  }

  function render(): void {
    const live = state.stream !== null;
    const hasPhoto = state.photo !== null;
    const active = live || hasPhoto;

    el.viewport.dataset['active'] = String(active);
    el.viewport.dataset['source'] = live ? 'camera' : hasPhoto ? 'photo' : 'none';
    el.viewportEmpty.classList.toggle('hidden', active);
    el.toggleCamera.textContent = live ? 'Stop camera' : 'Start camera';
    el.reset.disabled = state.baseline.size === 0;
    el.checkLight.disabled = !active;

    if (hasPhoto) {
      const { name, frame } = state.photo as { name: string; frame: ImageData };
      el.sourceNote.textContent =
        `Sampling "${name}" — ${frame.width}×${frame.height}, ` +
        `${sampleSizeFor(frame.width)} px sample region. ` +
        `Readings come from the file, so nothing about this scene can change between taps.`;
    } else if (live) {
      el.sourceNote.textContent =
        `Live camera — ${sampleSizeFor(el.video.videoWidth || 1920)} px sample region.`;
    } else {
      el.sourceNote.textContent = '';
    }
    el.checkLight.textContent = state.probingLight ? 'Done checking' : 'Check lighting';
    el.checkLight.classList.toggle('primary', state.probingLight);
    renderLighting();

    el.error.textContent = state.error ?? '';
    el.error.classList.toggle('hidden', state.error === null);

    const provenance = state.calibration?.provenance;
    el.provenance.dataset['state'] = provenance ?? 'none';
    el.provenance.textContent = provenance
      ? provenance === 'relative'
        ? 'Relative calibration'
        : 'Absolute calibration'
      : 'Uncalibrated';

    el.patchList.innerHTML = SWATCH_CATALOG.map((spec) => {
      const captured = state.baseline.get(spec.id);
      const pending = state.pendingPatchId === spec.id;
      const swatchStyle = captured
        ? `style="background-color:${srgbToHex(captured)};background-image:none"`
        : '';
      return `
        <div class="patch-row" data-pending="${pending}">
          <span class="chip" ${swatchStyle}></span>
          <span class="name">${escapeHtml(spec.name)}</span>
          <span class="status">${pending ? 'Tap the swatch' : captured ? srgbToHex(captured) : 'not sampled'}</span>
          <button data-patch="${escapeHtml(spec.id)}" ${active ? '' : 'disabled'}>
            ${captured ? 'Re-sample' : 'Sample'}
          </button>
        </div>`;
    }).join('');

    for (const button of el.patchList.querySelectorAll<HTMLButtonElement>('button[data-patch]')) {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        state.pendingPatchId = button.dataset['patch'] ?? null;
        render();
      });
    }

    const captured = state.baseline.size;
    const total = SWATCH_CATALOG.length;
    if (state.calibration) {
      const { model, meanDeltaE, maxDeltaE } = state.calibration;
      el.note.textContent =
        `${captured}/${total} patches · model "${model}" · in-sample fit mean ΔE₀₀ ` +
        `${meanDeltaE.toFixed(2)}, max ${maxDeltaE.toFixed(2)}. Readings are relative to this ` +
        `baseline, not absolute colour.` +
        (state.modelReason ? ` ${state.modelReason}` : '');
    } else {
      el.note.textContent =
        `${captured}/${total} patches sampled. Capture all three under the lighting you intend ` +
        `to work in; readings become comparable across sessions once calibrated.`;
    }
  }

  render();
}

function formatLab(lab: { L: number; a: number; b: number }): string {
  return `L ${lab.L.toFixed(2)}  a ${lab.a.toFixed(2)}  b ${lab.b.toFixed(2)}`;
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] as string,
  );
}

function query<T extends Element>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element: ${selector}`);
  return found;
}
